import { useEffect, useRef } from 'react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore lodash is in the parent dir
import _ from 'lodash';

import { QuoteCardType } from 'components/QuoteCard';
import { LaneHistoryResponse } from 'lib/api/getLaneHistory';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';

import { SelectedQuoteType } from '../types';
import { selectDefaultQuoteCard } from '../utils/selectDefaultQuoteCard';

// Automatic quote card selection based on service config, lane history, or lowest cost.
// Pass skipAutoSelection when the user picked a card or Kit Coach owns buy-rate selection.
export const useQuoteCardSelection = (
  quoteCards: QuoteCardType[],
  skipAutoSelection: boolean,
  isTMSLaneHistoryEnabled: boolean,
  laneHistory: Maybe<LaneHistoryResponse>,
  defaultFTLBuyRateSource: MaybeUndef<string>,
  setSelectedQuote: (quote: Maybe<SelectedQuoteType>) => void,
  setCarrierCost: (cost: number) => void
) => {
  const lastSelectionKeyRef = useRef<Maybe<string>>(null);

  useEffect(() => {
    if (!quoteCards?.length) {
      lastSelectionKeyRef.current = null;
      return;
    }

    if (skipAutoSelection) {
      return;
    }

    const hasLaneHistoryResults = Boolean(
      laneHistory && Object.values(laneHistory.resultsBySource ?? {}).length > 0
    );

    // A lane-data update replaces the cards array without changing the quotes.
    // Don't reselect the card and overwrite a buy rate the rep already entered.
    const selectionKey = [
      ...quoteCards.map(
        (card) => `${card.type}:${card.quoteId ?? ''}:${card.cost}`
      ),
      hasLaneHistoryResults,
      isTMSLaneHistoryEnabled,
      defaultFTLBuyRateSource ?? '',
    ].join('|');
    if (selectionKey === lastSelectionKeyRef.current) {
      return;
    }

    const selectedCard = selectDefaultQuoteCard({
      quoteCards,
      defaultFTLBuyRateSource,
      isTMSLaneHistoryEnabled,
      hasLaneHistoryResults,
    });

    if (!selectedCard) {
      return;
    }

    lastSelectionKeyRef.current = selectionKey;
    setSelectedQuote(selectedCard.type);
    setCarrierCost(_.round(selectedCard.cost, 2));
  }, [
    quoteCards,
    isTMSLaneHistoryEnabled,
    laneHistory,
    defaultFTLBuyRateSource,
    skipAutoSelection,
    setSelectedQuote,
    setCarrierCost,
  ]);
};
